import { useEffect, useState, useMemo } from "react";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  Button,
  Badge,
  Input,
  Label,
  ConfirmDialog,
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownMenuItem,
} from "@/components/ui";
import { PageHeader } from "@/components/ui/page-header";
import { usersApi, companiesApi } from "@/services/api";
import {
  Search,
  Users,
  MoreHorizontal,
  Loader2,
  X,
  Mail,
  Pencil,
  Trash2,
  UserCheck,
  UserX,
} from "lucide-react";
import { formatDate } from "@/lib/utils";

interface User {
  id: string;
  email: string;
  name: string;
  cpf?: string;
  role: "ADMIN" | "OPERATOR" | "VIEWER";
  isActive: boolean;
  companyId: string;
  companyName: string;
  createdAt: string;
  lastLoginAt: string | null;
  loginCount?: number;
}

interface Company {
  id: string;
  razaoSocial: string;
  status: string;
}

interface InviteForm {
  email: string;
  name: string;
  cpf: string;
  companyId: string;
  role: "ADMIN" | "OPERATOR" | "VIEWER";
}

interface EditForm {
  name: string;
  email: string;
  cpf: string;
  role: "ADMIN" | "OPERATOR" | "VIEWER";
}

const initialInviteForm: InviteForm = {
  email: "",
  name: "",
  cpf: "",
  companyId: "",
  role: "VIEWER",
};

const roleConfig = {
  ADMIN: { label: "Admin", variant: "default" as const },
  OPERATOR: { label: "Operador", variant: "secondary" as const },
  VIEWER: { label: "Visualizador", variant: "outline" as const },
};

const roleOptions = [
  { value: "ADMIN", label: "Admin", description: "Acesso total à empresa" },
  { value: "OPERATOR", label: "Operador", description: "Pode fazer operações" },
  { value: "VIEWER", label: "Visualizador", description: "Apenas leitura" },
];

// Função para formatar CPF
function formatCpf(value: string): string {
  const numbers = value.replace(/\D/g, "").slice(0, 11);
  if (numbers.length <= 3) return numbers;
  if (numbers.length <= 6) return `${numbers.slice(0, 3)}.${numbers.slice(3)}`;
  if (numbers.length <= 9) return `${numbers.slice(0, 3)}.${numbers.slice(3, 6)}.${numbers.slice(6)}`;
  return `${numbers.slice(0, 3)}.${numbers.slice(3, 6)}.${numbers.slice(6, 9)}-${numbers.slice(9)}`;
}

// Função para extrair apenas números do CPF
function extractCpfNumbers(value: string): string {
  return value.replace(/\D/g, "").slice(0, 11);
}

export function UsersPage() {
  const [users, setUsers] = useState<User[]>([]);
  const [companies, setCompanies] = useState<Company[]>([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");

  // Modal states
  const [showInviteModal, setShowInviteModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [selectedUser, setSelectedUser] = useState<User | null>(null);

  // Form states
  const [inviteForm, setInviteForm] = useState<InviteForm>(initialInviteForm);
  const [editForm, setEditForm] = useState<EditForm>({ name: "", email: "", cpf: "", role: "VIEWER" });
  const [inviting, setInviting] = useState(false);
  const [updating, setUpdating] = useState(false);
  const [formError, setFormError] = useState("");

  // Confirm dialog states
  const [confirmDialog, setConfirmDialog] = useState<{
    open: boolean;
    title: string;
    description: string;
    confirmText: string;
    variant: "default" | "danger";
    onConfirm: () => void;
    loading: boolean;
  }>({
    open: false,
    title: "",
    description: "",
    confirmText: "Confirmar",
    variant: "default",
    onConfirm: () => {},
    loading: false,
  });

  useEffect(() => {
    loadData();
  }, []);

  async function loadData() {
    try {
      const [usersData, companiesData] = await Promise.all([
        usersApi.list(),
        companiesApi.list(),
      ]);
      setUsers(usersData);
      // Apenas empresas ativas podem receber convites
      setCompanies(companiesData.filter((c: Company) => c.status === "ACTIVE"));
    } catch (error) {
      console.error("Erro ao carregar dados:", error);
    } finally {
      setLoading(false);
    }
  }

  async function handleInvite() {
    setFormError("");

    // Validations
    if (!inviteForm.email || !inviteForm.name || !inviteForm.companyId) {
      setFormError("Preencha todos os campos obrigatórios");
      return;
    }

    // Email validation
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(inviteForm.email)) {
      setFormError("Email inválido");
      return;
    }

    // CPF validation (opcional, mas se preenchido deve ter 11 dígitos)
    const cpfNumbers = extractCpfNumbers(inviteForm.cpf);
    if (inviteForm.cpf && cpfNumbers.length !== 11) {
      setFormError("CPF deve conter 11 dígitos");
      return;
    }

    setInviting(true);
    try {
      await usersApi.invite({
        ...inviteForm,
        cpf: cpfNumbers || undefined,
      });
      setInviteForm(initialInviteForm);
      setShowInviteModal(false);
      await loadData();
    } catch (error: any) {
      setFormError(error.message || "Erro ao convidar usuário");
    } finally {
      setInviting(false);
    }
  }

  async function handleUpdateUser() {
    if (!selectedUser) return;
    setFormError("");

    // Validations
    if (!editForm.name || !editForm.email) {
      setFormError("Nome e email são obrigatórios");
      return;
    }

    // Email validation
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(editForm.email)) {
      setFormError("Email inválido");
      return;
    }

    // CPF validation (opcional, mas se preenchido deve ter 11 dígitos)
    const cpfNumbers = extractCpfNumbers(editForm.cpf);
    if (editForm.cpf && cpfNumbers.length !== 11) {
      setFormError("CPF deve conter 11 dígitos");
      return;
    }

    setUpdating(true);
    try {
      await usersApi.update(selectedUser.id, {
        name: editForm.name,
        email: editForm.email,
        cpf: cpfNumbers || null,
        role: editForm.role,
      });
      setShowEditModal(false);
      setSelectedUser(null);
      await loadData();
    } catch (error: any) {
      setFormError(error.message || "Erro ao atualizar usuário");
    } finally {
      setUpdating(false);
    }
  }

  function openConfirmDialog(
    title: string,
    description: string,
    confirmText: string,
    variant: "default" | "danger",
    onConfirm: () => Promise<void>
  ) {
    setConfirmDialog({
      open: true,
      title,
      description,
      confirmText,
      variant,
      onConfirm: async () => {
        setConfirmDialog((prev) => ({ ...prev, loading: true }));
        try {
          await onConfirm();
          setConfirmDialog((prev) => ({ ...prev, open: false, loading: false }));
        } catch (error) {
          console.error("Erro na operação:", error);
          setConfirmDialog((prev) => ({ ...prev, loading: false }));
        }
      },
      loading: false,
    });
  }

  function closeConfirmDialog() {
    setConfirmDialog((prev) => ({ ...prev, open: false }));
  }

  function handleToggleActive(user: User) {
    const isActive = user.isActive;
    openConfirmDialog(
      isActive ? "Desativar Usuário" : "Ativar Usuário",
      isActive
        ? `Tem certeza que deseja desativar o usuário ${user.name}? Ele não poderá mais acessar o sistema.`
        : `Tem certeza que deseja ativar o usuário ${user.name}? Ele voltará a ter acesso ao sistema.`,
      isActive ? "Desativar" : "Ativar",
      isActive ? "danger" : "default",
      async () => {
        await usersApi.toggleActive(user.id);
        await loadData();
      }
    );
  }

  function handleDelete(user: User) {
    openConfirmDialog(
      "Excluir Usuário",
      `ATENÇÃO: Esta ação é irreversível. Todos os dados do usuário ${user.name} serão permanentemente removidos.`,
      "Excluir",
      "danger",
      async () => {
        await usersApi.delete(user.id);
        await loadData();
      }
    );
  }

  function openEditModal(user: User) {
    setSelectedUser(user);
    setEditForm({
      name: user.name,
      email: user.email,
      cpf: user.cpf ? formatCpf(user.cpf) : "",
      role: user.role,
    });
    setFormError("");
    setShowEditModal(true);
  }

  const filteredUsers = users.filter(
    (u) =>
      u.name.toLowerCase().includes(search.toLowerCase()) ||
      u.email.toLowerCase().includes(search.toLowerCase()) ||
      u.companyName?.toLowerCase().includes(search.toLowerCase())
  );

  // Calculate stats
  const stats = useMemo(() => {
    const total = users.length;
    const active = users.filter((u) => u.isActive).length;
    const inactive = users.filter((u) => !u.isActive).length;
    const admins = users.filter((u) => u.role === "ADMIN").length;
    return { total, active, inactive, admins };
  }, [users]);

  return (
    <div className="p-4 sm:p-6 space-y-4 sm:space-y-6">
      <PageHeader
        icon={<Users className="w-5 h-5 sm:w-6 sm:h-6" />}
        title="Usuários"
        description="Gerenciamento de usuários das empresas"
        stats={[
          { value: stats.total, label: "Total" },
          { value: stats.active, label: "Ativos" },
          { value: stats.inactive, label: "Inativos" },
          { value: stats.admins, label: "Admins" },
        ]}
        actions={
          <Button
            onClick={() => {
              setInviteForm(initialInviteForm);
              setFormError("");
              setShowInviteModal(true);
            }}
            className="bg-white text-blue-600 hover:bg-blue-50"
          >
            <Mail className="w-4 h-4 mr-2" />
            Convidar Usuário
          </Button>
        }
      />

      {/* Search Bar */}
      <div className="relative max-w-md">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
        <Input
          placeholder="Buscar por nome, email, empresa..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          className="pl-9"
        />
      </div>

      {/* Users Table */}
      <Card>
        <CardContent className="p-0">
            {loading ? (
              <div className="p-8 text-center text-muted-foreground">
                <Loader2 className="w-6 h-6 animate-spin mx-auto mb-2" />
                Carregando...
              </div>
            ) : filteredUsers.length === 0 ? (
              <div className="p-8 text-center text-muted-foreground">
                <Users className="w-12 h-12 mx-auto mb-4 opacity-50" />
                <p>Nenhum usuário encontrado</p>
              </div>
            ) : (
              <table className="w-full">
                <thead className="border-b border-border">
                  <tr className="text-left text-sm text-muted-foreground">
                    <th className="p-4 font-medium">Usuário</th>
                    <th className="p-4 font-medium">Empresa</th>
                    <th className="p-4 font-medium">Role</th>
                    <th className="p-4 font-medium">Status</th>
                    <th className="p-4 font-medium">Acessos</th>
                    <th className="p-4 font-medium">Último acesso</th>
                    <th className="p-4 font-medium">Ações</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredUsers.map((user) => (
                    <tr
                      key={user.id}
                      className="border-b border-border last:border-0 hover:bg-muted/50"
                    >
                      <td className="p-4">
                        <div>
                          <p className="font-medium">{user.name}</p>
                          <p className="text-sm text-muted-foreground">
                            {user.email}
                          </p>
                        </div>
                      </td>
                      <td className="p-4 text-sm">{user.companyName || "-"}</td>
                      <td className="p-4">
                        <Badge variant={roleConfig[user.role].variant}>
                          {roleConfig[user.role].label}
                        </Badge>
                      </td>
                      <td className="p-4">
                        <Badge variant={user.isActive ? "success" : "secondary"}>
                          {user.isActive ? "Ativo" : "Inativo"}
                        </Badge>
                      </td>
                      <td className="p-4 text-sm text-muted-foreground">
                        {user.loginCount ?? 0}
                      </td>
                      <td className="p-4 text-sm text-muted-foreground">
                        {user.lastLoginAt ? formatDate(user.lastLoginAt) : "Nunca"}
                      </td>
                      <td className="p-4">
                        <DropdownMenu>
                          <DropdownMenuTrigger asChild>
                            <Button size="sm" variant="ghost">
                              <MoreHorizontal className="w-4 h-4" />
                            </Button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent align="end" className="min-w-[160px]">
                            <DropdownMenuItem onClick={() => openEditModal(user)}>
                              <Pencil className="w-3 h-3 mr-2" />
                              Editar Usuário
                            </DropdownMenuItem>
                            <DropdownMenuItem onClick={() => handleToggleActive(user)}>
                              {user.isActive ? (
                                <>
                                  <UserX className="w-3 h-3 mr-2" />
                                  Desativar
                                </>
                              ) : (
                                <>
                                  <UserCheck className="w-3 h-3 mr-2" />
                                  Ativar
                                </>
                              )}
                            </DropdownMenuItem>
                            <DropdownMenuItem
                              onClick={() => handleDelete(user)}
                              className="text-destructive focus:text-destructive"
                            >
                              <Trash2 className="w-3 h-3 mr-2" />
                              Excluir
                            </DropdownMenuItem>
                          </DropdownMenuContent>
                        </DropdownMenu>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
        </CardContent>
      </Card>

      {/* Invite Modal - Layout Horizontal */}
      {showInviteModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <Card className="w-full max-w-2xl">
            <CardHeader className="flex flex-row items-center justify-between pb-4">
              <CardTitle>Convidar Usuário</CardTitle>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setShowInviteModal(false)}
              >
                <X className="w-4 h-4" />
              </Button>
            </CardHeader>
            <CardContent className="space-y-4">
              {formError && (
                <div className="p-3 text-sm text-destructive bg-destructive/10 rounded-md">
                  {formError}
                </div>
              )}

              {/* Linha 1: Email e Nome */}
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="invite-email">Email *</Label>
                  <Input
                    id="invite-email"
                    type="email"
                    placeholder="usuario@empresa.com"
                    value={inviteForm.email}
                    onChange={(e) =>
                      setInviteForm({ ...inviteForm, email: e.target.value })
                    }
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="invite-name">Nome *</Label>
                  <Input
                    id="invite-name"
                    placeholder="Nome completo"
                    value={inviteForm.name}
                    onChange={(e) =>
                      setInviteForm({ ...inviteForm, name: e.target.value })
                    }
                  />
                </div>
              </div>

              {/* Linha 2: CPF e Empresa */}
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="invite-cpf">CPF</Label>
                  <Input
                    id="invite-cpf"
                    placeholder="000.000.000-00"
                    value={inviteForm.cpf}
                    onChange={(e) =>
                      setInviteForm({ ...inviteForm, cpf: formatCpf(e.target.value) })
                    }
                    maxLength={14}
                  />
                  <p className="text-xs text-muted-foreground">Necessário para Open Finance</p>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="invite-company">Empresa *</Label>
                  <select
                    id="invite-company"
                    className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
                    value={inviteForm.companyId}
                    onChange={(e) =>
                      setInviteForm({ ...inviteForm, companyId: e.target.value })
                    }
                  >
                    <option value="">Selecione uma empresa</option>
                    {companies.map((company) => (
                      <option key={company.id} value={company.id}>
                        {company.razaoSocial}
                      </option>
                    ))}
                  </select>
                </div>
              </div>

              {/* Linha 3: Função (horizontal) */}
              <div className="space-y-2">
                <Label>Função *</Label>
                <div className="grid grid-cols-3 gap-3">
                  {roleOptions.map((option) => (
                    <label
                      key={option.value}
                      className={`flex items-center p-3 border rounded-md cursor-pointer ${
                        inviteForm.role === option.value
                          ? "border-primary bg-primary/5"
                          : "border-border hover:bg-muted/50"
                      }`}
                    >
                      <input
                        type="radio"
                        name="role"
                        value={option.value}
                        checked={inviteForm.role === option.value}
                        onChange={(e) =>
                          setInviteForm({
                            ...inviteForm,
                            role: e.target.value as "ADMIN" | "OPERATOR" | "VIEWER",
                          })
                        }
                        className="mr-3"
                      />
                      <div>
                        <p className="font-medium text-sm">{option.label}</p>
                        <p className="text-xs text-muted-foreground">
                          {option.description}
                        </p>
                      </div>
                    </label>
                  ))}
                </div>
              </div>

              <div className="p-3 bg-muted rounded-md text-sm text-muted-foreground">
                Uma senha temporária será enviada por email para o usuário.
              </div>

              <div className="flex justify-end gap-2 pt-2">
                <Button
                  variant="outline"
                  onClick={() => setShowInviteModal(false)}
                >
                  Cancelar
                </Button>
                <Button onClick={handleInvite} disabled={inviting}>
                  {inviting && <Loader2 className="w-4 h-4 animate-spin mr-2" />}
                  Enviar Convite
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Edit User Modal - Layout Horizontal */}
      {showEditModal && selectedUser && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <Card className="w-full max-w-2xl">
            <CardHeader className="flex flex-row items-center justify-between pb-4">
              <CardTitle>Editar Usuário</CardTitle>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setShowEditModal(false)}
              >
                <X className="w-4 h-4" />
              </Button>
            </CardHeader>
            <CardContent className="space-y-4">
              {formError && (
                <div className="p-3 text-sm text-destructive bg-destructive/10 rounded-md">
                  {formError}
                </div>
              )}

              {/* Info da empresa (não editável) */}
              <div className="p-3 bg-muted rounded-md">
                <p className="text-sm text-muted-foreground">Empresa</p>
                <p className="font-medium">{selectedUser.companyName}</p>
              </div>

              {/* Linha 1: Nome e Email */}
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="edit-name">Nome *</Label>
                  <Input
                    id="edit-name"
                    placeholder="Nome completo"
                    value={editForm.name}
                    onChange={(e) =>
                      setEditForm({ ...editForm, name: e.target.value })
                    }
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="edit-email">Email *</Label>
                  <Input
                    id="edit-email"
                    type="email"
                    placeholder="usuario@empresa.com"
                    value={editForm.email}
                    onChange={(e) =>
                      setEditForm({ ...editForm, email: e.target.value })
                    }
                  />
                </div>
              </div>

              {/* Linha 2: CPF */}
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="edit-cpf">CPF</Label>
                  <Input
                    id="edit-cpf"
                    placeholder="000.000.000-00"
                    value={editForm.cpf}
                    onChange={(e) =>
                      setEditForm({ ...editForm, cpf: formatCpf(e.target.value) })
                    }
                    maxLength={14}
                  />
                  <p className="text-xs text-muted-foreground">Necessário para Open Finance</p>
                </div>
                <div></div>
              </div>

              {/* Linha 3: Função (horizontal) */}
              <div className="space-y-2">
                <Label>Função *</Label>
                <div className="grid grid-cols-3 gap-3">
                  {roleOptions.map((option) => (
                    <label
                      key={option.value}
                      className={`flex items-center p-3 border rounded-md cursor-pointer ${
                        editForm.role === option.value
                          ? "border-primary bg-primary/5"
                          : "border-border hover:bg-muted/50"
                      }`}
                    >
                      <input
                        type="radio"
                        name="editRole"
                        value={option.value}
                        checked={editForm.role === option.value}
                        onChange={(e) =>
                          setEditForm({
                            ...editForm,
                            role: e.target.value as "ADMIN" | "OPERATOR" | "VIEWER",
                          })
                        }
                        className="mr-3"
                      />
                      <div>
                        <p className="font-medium text-sm">{option.label}</p>
                        <p className="text-xs text-muted-foreground">
                          {option.description}
                        </p>
                      </div>
                    </label>
                  ))}
                </div>
              </div>

              <div className="flex justify-end gap-2 pt-2">
                <Button
                  variant="outline"
                  onClick={() => setShowEditModal(false)}
                >
                  Cancelar
                </Button>
                <Button onClick={handleUpdateUser} disabled={updating}>
                  {updating && <Loader2 className="w-4 h-4 animate-spin mr-2" />}
                  Salvar Alterações
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Confirm Dialog */}
      <ConfirmDialog
        open={confirmDialog.open}
        onClose={closeConfirmDialog}
        onConfirm={confirmDialog.onConfirm}
        title={confirmDialog.title}
        description={confirmDialog.description}
        confirmText={confirmDialog.confirmText}
        variant={confirmDialog.variant}
        loading={confirmDialog.loading}
      />
    </div>
  );
}
